import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Plus } from "@air/next-icons";
import { Button } from "@air/primitive-button";
import { IconButton } from "@air/primitive-icon-button";
import { Tooltip } from "@air/primitive-tooltip";
import { memo, useCallback, useEffect, useState } from "react";
import { BoardSelectCreateInput } from "~/components/BoardSelect/components/BoardSelectCreateInput";
import { BoardSelectListItem } from "~/components/BoardSelect/components/BoardSelectListItem";
import { useBoardPermissions } from "~/hooks/useBoardPermissions";
import { useFetchObjectsPermissions } from "~/hooks/useFetchObjectsPermissions";
import { useCreateNewBoard } from "~/swr-hooks/boards/useCreateNewBoard";
import { useSubBoardsList } from "~/swr-hooks/boards/useSubBoardsList";
import { canCreateBoard } from "~/utils/permissions/boardPermissions";
const CREATE_SUB_BOARD_LABEL = "Create New Sub-Board";
export const BoardSelectBoardListItem = /*#__PURE__*/ memo((param)=>{
    let { board, getBoardDisabledMessage, isBoardDisabled, isBoardExpanded, isBoardSelected, onBoardOpenChange, onSelectBoard, shouldShowSubBoards, search } = param;
    var _board_library;
    const isDisabled = isBoardDisabled === null || isBoardDisabled === void 0 ? void 0 : isBoardDisabled(board);
    const isSelected = isBoardSelected === null || isBoardSelected === void 0 ? void 0 : isBoardSelected(board);
    const isExpanded = isBoardExpanded === null || isBoardExpanded === void 0 ? void 0 : isBoardExpanded(board.id);
    const [isCreatingSubBoard, setIsCreatingSubBoard] = useState(false);
    const { boardPermissions } = useBoardPermissions({
        boardId: board.id
    });
    const { createNewBoard } = useCreateNewBoard();
    const { data: subBoards, isInitialLoading: isLoadingSubBoards, hasMore: hasMoreSubBoards, loadNextPage: loadNextSubBoardsPage } = useSubBoardsList(board.id, Boolean(isExpanded), {
        search
    });
    useFetchObjectsPermissions({
        objects: {
            boardIds: subBoards.map((board)=>board.id)
        }
    });
    const onDraftSubBoard = useCallback(()=>{
        onBoardOpenChange({
            id: board.id,
            isOpen: true
        });
        setIsCreatingSubBoard(true);
    }, [
        board.id,
        onBoardOpenChange
    ]);
    const onSubBoardCreate = useCallback((boardName)=>{
        setIsCreatingSubBoard(false);
        if (boardName) {
            var _board_library;
            createNewBoard({
                board: {
                    title: boardName,
                    parentId: board.id,
                    libraryId: (_board_library = board.library) === null || _board_library === void 0 ? void 0 : _board_library.id
                },
                trackLocation: "board-select-modal",
                shouldFollow: true
            });
        }
    }, [
        board.id,
        (_board_library = board.library) === null || _board_library === void 0 ? void 0 : _board_library.id,
        createNewBoard
    ]);
    useEffect(()=>{
        if (shouldShowSubBoards === null || shouldShowSubBoards === void 0 ? void 0 : shouldShowSubBoards(board.id)) {
            onBoardOpenChange({
                id: board.id,
                isOpen: true
            });
        }
    }, [
        board.id,
        onBoardOpenChange,
        shouldShowSubBoards
    ]);
    return /*#__PURE__*/ _jsxs(BoardSelectListItem, {
        disabled: isDisabled,
        disabledMessage: getBoardDisabledMessage === null || getBoardDisabledMessage === void 0 ? void 0 : getBoardDisabledMessage(board),
        id: board.id,
        isExpanded: isExpanded || isCreatingSubBoard,
        isSelected: isSelected,
        onOpenChange: (isOpen)=>onBoardOpenChange({
                id: board.id,
                isOpen
            }),
        onSelect: ()=>onSelectBoard === null || onSelectBoard === void 0 ? void 0 : onSelectBoard(isSelected ? null : board),
        suffix: !isDisabled && canCreateBoard(boardPermissions) && /*#__PURE__*/ _jsx(Tooltip, {
            label: CREATE_SUB_BOARD_LABEL,
            children: /*#__PURE__*/ _jsx(IconButton, {
                appearance: "ghost",
                className: "hidden group-hover/treeItem:flex",
                color: "grey",
                icon: Plus,
                label: CREATE_SUB_BOARD_LABEL,
                onClick: onDraftSubBoard,
                size: "small"
            })
        }),
        title: board.title,
        children: [
            isCreatingSubBoard && /*#__PURE__*/ _jsx(BoardSelectCreateInput, {
                onBlur: ()=>setIsCreatingSubBoard(false),
                onSubmit: onSubBoardCreate
            }),
            subBoards.map((subBoard)=>{
                return /*#__PURE__*/ _jsx(BoardSelectBoardListItem, {
                    board: subBoard,
                    getBoardDisabledMessage: getBoardDisabledMessage,
                    isBoardDisabled: isBoardDisabled,
                    isBoardExpanded: isBoardExpanded,
                    isBoardSelected: isBoardSelected,
                    onBoardOpenChange: onBoardOpenChange,
                    onSelectBoard: onSelectBoard,
                    shouldShowSubBoards: shouldShowSubBoards,
                    search: search
                }, subBoard.id);
            }),
            !isCreatingSubBoard && isExpanded && !subBoards.length && !isLoadingSubBoards && /*#__PURE__*/ _jsx(Button, {
                appearance: "ghost",
                className: "mt-1",
                color: "grey",
                onClick: onDraftSubBoard,
                size: "small",
                children: CREATE_SUB_BOARD_LABEL
            }),
            !isLoadingSubBoards && hasMoreSubBoards && /*#__PURE__*/ _jsx(Button, {
                className: "mt-1",
                onClick: ()=>loadNextSubBoardsPage(),
                size: "small",
                appearance: "ghost",
                color: "grey",
                children: "Show more"
            })
        ]
    });
});
BoardSelectBoardListItem.displayName = "BoardSelectBoardListItem";
